import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg } from "@/lib/api";
import { hasData, POINT_LEVELS, toSubscript, TYPE_LABELS } from "@/lib/chem";
import { ClassificationFields, defaultData, GuessFields, MatchingFields } from "@/components/teacher/StructuredFields";
import { SubscriptBar } from "@/components/teacher/SubscriptBar";
import { OptionFields } from "@/components/teacher/OptionFields";
import { ImageField } from "@/components/teacher/ImageField";
import { ConsentCheck, EthicsLink } from "@/components/Legal";

const opt = (id, text = "") => ({ id, text, misconception: "", hint: "" });
const blank = () => ({
  type: "multiple_choice", text: "", options: ["a", "b", "c", "d"].map((i) => opt(i)), correct_option_id: "a",
  points: 10, time_limit: 60, explanation: "", image: null, topic: "", data: {},
});

const withType = (form, type) => {
  if (type === form.type) return form;
  if (hasData(type)) return { ...form, type, options: [], correct_option_id: null, data: defaultData(type) };
  if (type === "true_false") return { ...form, type, options: [opt("a", "Doğru"), opt("b", "Yanlış")], correct_option_id: "a", data: {} };
  return { ...form, type, options: ["a", "b", "c", "d"].map((i) => opt(i)), correct_option_id: "a", data: {} };
};

const Section = ({ title, children }) => (
  <section className="space-y-3">
    <h4 className="eyebrow text-[#FACC15]">{title}</h4>
    {children}
  </section>
);

function useFieldTracker(form, setForm) {
  const active = useRef(null);
  const get = (k) => {
    if (k.startsWith("a:")) return form.data.answers[+k.slice(2)] || "";
    if (k.startsWith("d:")) { const [, arr, i, field] = k.split(":"); return form.data[arr][+i][field] || ""; }
    return (k.startsWith("opt-") ? form.options[+k.split("-")[1]][k.split("-")[2]] : form[k]) || "";
  };
  const set = (k, v) => setForm((f) => {
    if (k.startsWith("a:")) return { ...f, data: { answers: f.data.answers.map((x, j) => (j === +k.slice(2) ? v : x)) } };
    if (k.startsWith("d:")) {
      const [, arr, i, field] = k.split(":");
      return { ...f, data: { ...f.data, [arr]: f.data[arr].map((x, j) => (j === +i ? { ...x, [field]: v } : x)) } };
    }
    if (!k.startsWith("opt-")) return { ...f, [k]: v };
    const [, i, field] = k.split("-");
    return { ...f, options: f.options.map((o, j) => (j === +i ? { ...o, [field]: v } : o)) };
  });
  const apply = (fn) => {
    const cur = active.current;
    if (!cur || cur.el.disabled) return toast.info("Önce bir metin alanına tıklayın");
    const { key, el } = cur;
    const v = get(key);
    const s = el.selectionStart ?? v.length;
    const e = el.selectionEnd ?? v.length;
    const mid = fn(v.slice(s, e));
    set(key, v.slice(0, s) + mid + v.slice(e));
    requestAnimationFrame(() => { el.focus(); el.setSelectionRange(s + mid.length, s + mid.length); });
  };
  return {
    track: (key, el) => { active.current = { key, el }; },
    insert: (c) => apply(() => c),
    formula: () => apply((sel) => toSubscript(sel)),
  };
}

const TypeSwitch = ({ form, setForm }) => (
  <div className="flex flex-wrap gap-2">
    {Object.entries(TYPE_LABELS).map(([t, l]) => (
      <button key={t} type="button" onClick={() => setForm((f) => withType(f, t))} data-testid={`question-type-${t}`}
        className={`px-4 py-2 rounded-lg border font-semibold ${form.type === t ? "bg-[#FACC15] text-[#05070D] border-[#FACC15]" : "border-[rgba(71,85,105,0.45)] hover:border-[#FACC15]/60"}`}>
        {l}
      </button>
    ))}
  </div>
);

const ScoreFields = ({ form, update, track, topics }) => (
  <div className="grid sm:grid-cols-3 gap-4">
    <div className="space-y-2">
      <Label>Puan seviyesi</Label>
      <div className="flex gap-1.5 flex-wrap">
        {POINT_LEVELS.map((p) => (
          <button key={p} type="button" onClick={() => update({ points: p })} data-testid={`question-points-${p}`}
            className={`h-10 px-3 rounded-md border font-bold ${form.points === p ? "bg-[#34D399] text-[#05070D] border-[#34D399]" : "border-[rgba(71,85,105,0.45)]"}`}>{p}</button>
        ))}
        <Input type="number" min={1} max={1000} value={form.points} onChange={(e) => update({ points: Number(e.target.value) })}
          className="h-10 w-20" data-testid="question-points-input" aria-label="Özel puan" />
      </div>
    </div>
    <div className="space-y-2">
      <Label htmlFor="q-time">Süre (sn)</Label>
      <Input id="q-time" type="number" min={5} max={600} value={form.time_limit} onChange={(e) => update({ time_limit: Number(e.target.value) })}
        className="h-10" data-testid="question-time-input" />
    </div>
    <div className="space-y-2">
      <Label htmlFor="q-topic">Konu etiketi</Label>
      <Input id="q-topic" list="topic-list" value={form.topic} onFocus={(e) => track("topic", e.target)}
        onChange={(e) => update({ topic: e.target.value })} placeholder="örn. Element sembolleri" className="h-10" data-testid="question-topic-input" />
      <datalist id="topic-list">{topics.map((t) => <option key={t} value={t} />)}</datalist>
    </div>
  </div>
);

export const QuestionEditor = ({ open, onOpenChange, poolId, question, topics, onSaved }) => {
  const [form, setForm] = useState(blank());
  const [busy, setBusy] = useState(false);
  const [ethics, setEthics] = useState(false);
  const { track, insert, formula } = useFieldTracker(form, setForm);
  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  useEffect(() => {
    if (open) setForm(question ? { ...blank(), ...structuredClone(question) } : blank());
  }, [open, question]);

  const save = async () => {
    setBusy(true);
    try {
      const { id, pool_id, order, created_at, updated_at, ...body } = form;
      if (question) await api.put(`/questions/${question.id}`, body);
      else await api.post(`/pools/${poolId}/questions`, body);
      toast.success(question ? "Soru güncellendi" : "Soru eklendi");
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[rgba(15,23,42,0.55)] border-[rgba(71,85,105,0.45)] max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="question-editor">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl">{question ? "Soruyu düzenle" : "Soru ekle"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-7">
          <div className="sticky top-0 z-10 -mx-1 px-1 py-1 bg-slate-950/90 backdrop-blur-md rounded-xl"><SubscriptBar onInsert={insert} onFormula={formula} /></div>
          <Section title="Soru">
            <TypeSwitch form={form} setForm={setForm} />
            <Textarea value={form.text} onFocus={(e) => track("text", e.target)} onChange={(e) => update({ text: e.target.value })}
              placeholder="Soru metni" className="min-h-24 text-lg" data-testid="question-text-input" />
            <ImageField value={form.image} onChange={(image) => update({ image })} />
          </Section>
          {form.type === "matching" && <Section title="Eşleştirme çiftleri"><MatchingFields form={form} update={update} track={track} /></Section>}
          {form.type === "classification" && <Section title="Gruplar ve öğeler"><ClassificationFields form={form} update={update} track={track} /></Section>}
          {form.type === "guess" && <Section title="Kabul edilen cevaplar"><GuessFields form={form} update={update} track={track} /></Section>}
          {!hasData(form.type) && <Section title="Şıklar"><OptionFields form={form} update={update} track={track} /></Section>}
          <Section title="Puan ve ayarlar"><ScoreFields form={form} update={update} track={track} topics={topics} /></Section>
          <Section title="Açıklama (doğru cevap gösterilirken görünür)">
            <Textarea value={form.explanation} onFocus={(e) => track("explanation", e.target)} onChange={(e) => update({ explanation: e.target.value })}
              placeholder="İsteğe bağlı" className="min-h-20" data-testid="question-explanation-input" />
          </Section>
        </div>
        <ConsentCheck checked={ethics} onChange={setEthics} testId="question-ethics">
          Eklediğim içeriğin <EthicsLink />'na uygun olduğunu, ticari yayınevlerine (ÖSYM, MEB veya özel yayınlar) ait telifli materyalleri izinsiz kopyalamadığımı ve içeriğin tüm yasal sorumluluğunun şahsıma ait olduğunu kabul ediyorum.
        </ConsentCheck>
        <DialogFooter className="pt-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="question-cancel-button">Vazgeç</Button>
          <Button onClick={save} disabled={busy || !ethics} className="font-bold text-base px-6" data-testid="question-save-button">Kaydet</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
